import { useState, useEffect, createContext, useContext } from 'react';
import { API } from 'aws-amplify';
import format from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { useApiError } from './ApiErrorProvider';
import { error } from '../constants/bindings';
import { getMonthDay } from '../helpers/dateHelpers';
import {
  PairingDate,
  PairingSettings,
  SelectedDates
} from '../models/Exception';

const documentDefault = {
  filestats: [],
  acceptableTypes: []
};

const AdminContext = createContext({
  getPairingFrezzeData: (date) => {},
  savePairingException: (date, settings, execption) => {},
  pairingFreezeData: null as any,
  loading: true,
  getDocumentData: () => {},
  saveUpload: (file): any => {},
  documentsAndTypes: documentDefault as any
});

const pairingFreezeDefault = {
  pairingFreeze: []
};

export const AdminProvider = (props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [pairingFreezeData, setPairingFreezeData] =
    useState(pairingFreezeDefault);
  const [documentsAndTypes, setDocumentsAndTypes] = useState(
    documentDefault as any
  );
  const { setErrorCode } = useApiError();

  useEffect(() => {
    getDocumentData();
  }, []);

  const getPairingFrezzeData = async (date) => {
    const requestObject = {
      body: {
        pairingYear: date.year,
        pairingMonth: date.month
      }
    };
    try {
      setLoading(true);
      const pairingFrezzeData = await API.post(
        'crewlife',
        '/freezeperiod/retrieve',
        requestObject
      );
      const pairingFreezes = pairingFrezzeData.pairingFreeze.map(
        (row, index) => ({
          ...row,
          id: index,
          exception: row.biddingClosed.isAdjusted ? t('Admin.exception') : '',
          blockMonthDates: `${getMonthDay(row.blockdates.start)} - ${getMonthDay(row.blockdates.end)}`,
          blockCloseTimeTZ: `${row.biddingClosed.time} ${row.biddingClosed.tz}`,
          pairingPostedOn: row.pairingPosted.start,
          // pairingFreezedOn: row.pairingFreezed.start ,
          biddingClosedOn: row.biddingClosed.start
        })
      );
      setPairingFreezeData({
        ...pairingFreezeData,
        pairingFreeze: pairingFreezes
      });
      setLoading(false);
    } catch (e: any) {
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const fillZeroTime = (time: number) => {
    return time < 10 ? `0${time}` : `${time}`;
  };

  const formatTime = (date: Date) => {
    return `${fillZeroTime(date.getHours())}:${fillZeroTime(date.getMinutes())}:00`;
  };

  const savePairingException = async (
    yearMonth: PairingDate,
    settings: PairingSettings,
    selectedDates: SelectedDates
  ) => {
    const requestObject = {
      body: {
        ...yearMonth,
        ...settings,
        pairingPostedOn: format(selectedDates.pairingPostedOn, 'yyyy-MM-dd'),
        // pairingFreezedOn: format(getMiddleDate(selectedDates.pairingPostedOn, selectedDates.biddingClosedOn), 'yyyy-MM-dd'),
        biddingClosedOn: format(selectedDates.biddingClosedOn, 'yyyy-MM-dd'),
        blockCloseTime: formatTime(selectedDates.blockCloseTime),
        blockCloseTZ: selectedDates.blockCloseTZ
      }
    };

    try {
      setLoading(true);
      await API.post('crewlife', '/freezeperiod/save', requestObject);
    } catch (e: any) {
      setErrorCode(error.DEFAULT);
      setLoading(false);
      //return console.error('Request freezeperiod/save failed', e);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentData = async () => {
    try {
      setLoading(true);
      const documentTypes = await API.get('crewlife', '/documenttype', {});
      setDocumentsAndTypes(documentTypes);
    } catch (e) {
      console.error('Get documents error: ', e);
    } finally {
      setLoading(false);
    }
  };

  const saveUpload = async (file: any) => {
    const formData = new FormData();
    let statusCode = 0;
    formData.append('File', file);
    const requestObject = {
      body: formData
    };

    try {
      setLoading(true);
      await API.post('crewlife-upload', `/uploads3/file`, requestObject)
        .then((response) => {})
        .catch((error) => {
          console.error(error);
          statusCode = error.response?.data?.errors?.Error.errorCode;
        });
    } catch (e: any) {
      console.error('Upload file error: ', e);
      statusCode = e.response?.status;
    } finally {
      setLoading(false);
    }
    return statusCode;
  };

  return (
    <AdminContext.Provider
      value={{
        loading,
        getPairingFrezzeData,
        savePairingException,
        pairingFreezeData,
        getDocumentData,
        saveUpload,
        documentsAndTypes
      }}
    >
      {props.children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  return useContext(AdminContext);
};
